import React, { useState, useEffect } from "react";
import {
  Users,
  ListChecks,
  Bell,
  Image as ImageIcon,
  UserPlus,
  ClipboardList,
  LogOut,
  ChevronDown,
  Search,
  Plus,
  FileSpreadsheet,
  Mail,
  Trash2,
  Pencil,
  Send,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import '../Styles/dashboard.css';

// Change this if your backend runs on a different host/port
const API_BASE_URL = "";

const NAV = [
  {
    section: null,
    items: [
      {
        label: "Dashboard",
        icon: "/assets/dash_icon.png",
        isImage: true,
      },
    ],
  },
  {
    section: "Community",
    items: [
      { label: "Community Members List", icon: "/assets/commu.png", isImage: true },
      { label: "Collaborate List", icon: "/assets/collab.png", isImage: true },
      { label: "Subscribers", icon: "/assets/sub.png", isImage: true },
    ],
  },
  {
    section: "Gallery",
    items: [{ label: "Upload Event Photos", icon: "/assets/upload.png", isImage: true }],
  },
  {
    section: "HR Events 2026",
    items: [
      {
        label: "Registration List",
        icon: "/assets/register.png",
        isImage: true,
        active: true,
        expandable: true,
      },
      { label: "Attendees List", icon: "/assets/attend.png", isImage: true, expandable: true },
    ],
  },
];

export default function RegisteredUsersDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState("");

  // Track per-row "sending..." state for the Send Confirmation button
  const [sendingId, setSendingId] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/registrations`);
      if (!res.ok) throw new Error("Failed to load registered users");
      const data = await res.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Could not load registrations. Is the backend running?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((u) =>
    u.fullName?.toLowerCase().includes(query.toLowerCase())
  );

  const toggleAll = () => {
    setSelectAll((v) => !v);
    setSelected(selectAll ? [] : filteredUsers.map((u) => u.id));
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSendConfirmation = async (id) => {
    setSendingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/registrations/${id}/resend-email`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to resend email");
      await fetchUsers(); // refresh so the "Email Sent" pill updates
    } catch (err) {
      console.error(err);
      alert("Failed to send confirmation email. Please try again.");
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this registration? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/registrations/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete registration. Please try again.");
    }
  };

  const total = users.length;
  const emailsSentCount = users.filter((u) => u.emailSent).length;

  return (
    <div className="hrd-app">
      {/* ---------------- Sidebar ---------------- */}
      <aside className="hrd-sidebar">
        <div className="hrd-brand">
          <img src="/assets/vana.png" alt="Vanakkam HRD" className="hrd-logo" />
        </div>

        <nav className="hrd-nav">
          {NAV.map((group, gi) => (
            <div className="hrd-nav-group" key={gi}>
              {group.section && <div className="hrd-nav-label">{group.section}</div>}
              {group.items.map((item) => (
                <button
                  key={item.label}
                  className={`hrd-nav-item${item.active ? " active" : ""}`}
                >
                  {item.isImage ? (
                    <img src={item.icon} alt={item.label} className="hrd-nav-icon-img" />
                  ) : (
                    <item.icon size={16} strokeWidth={2} />
                  )}
                  <span>{item.label}</span>
                  {item.expandable && (
                    <ChevronDown size={14} className="hrd-nav-chevron" />
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* ---------------- Main content ---------------- */}
      <main className="hrd-main">
        <header className="hrd-topbar hrd-panel">
          <h1>
            Registered Users&ensp;–&ensp;
            <span className="hrd-topbar-sub">India HR Confluence 2026</span>
          </h1>
          <button className="hrd-logout">
            <LogOut size={14} />
            Logout
          </button>
        </header>

        <section className="hrd-panel">
          {/* Stat pills */}
          <div className="hrd-stats">
            <span className="hrd-pill hrd-pill--amber">Total Registered : {total}</span>
            <span className="hrd-pill hrd-pill--outline">Emails Sent : {emailsSentCount}</span>
            <span className="hrd-pill hrd-pill--outline">
              Emails Unsent : {total - emailsSentCount}
            </span>
            <button className="hrd-pill hrd-pill--action">
              <FileSpreadsheet size={14} />
              Export To Excel
            </button>
            <button className="hrd-pill hrd-pill--action-outline">
              <Mail size={14} />
              Sent Bulk Email
            </button>
          </div>

          {/* Bulk action buttons */}
          <div className="hrd-actions">
            <button className="hrd-btn hrd-btn--outline">
              <Plus size={14} />
              Add Bulk Sponsor Users
            </button>
            <button className="hrd-btn hrd-btn--outline">
              <Plus size={14} />
              Add Bulk Special Users
            </button>
            <button className="hrd-btn hrd-btn--danger">
              <Trash2 size={14} />
              Bulk Delete
            </button>
          </div>

          {/* Table controls */}
          <div className="hrd-table-controls">
            <label className="hrd-show-entries">
              Show Entries:
              <select defaultValue={20}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
            <div className="hrd-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="hrd-table-wrap">
            {loading ? (
              <p style={{ padding: "20px" }}>Loading registered users...</p>
            ) : error ? (
              <p style={{ padding: "20px", color: "#e0432c" }}>{error}</p>
            ) : (
              <table className="hrd-table">
                <thead>
                  <tr>
                    <th className="hrd-checkbox-col">
                      <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                    </th>
                    <th>S.No</th>
                    <th>Full Name</th>
                    <th>Designation</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Personal Email</th>
                    <th>Official Email</th>
                    <th>Email Sent</th>
                    <th>Send Confirmation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ padding: "20px", textAlign: "center" }}>
                        No registrations yet.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u, index) => (
                      <tr key={u.id}>
                        <td className="hrd-checkbox-col">
                          <input
                            type="checkbox"
                            checked={selected.includes(u.id)}
                            onChange={() => toggleOne(u.id)}
                          />
                        </td>
                        <td>{index + 1}</td>
                        <td>
                          <div className="hrd-user-cell">
                            <span className="hrd-avatar">{u.fullName?.charAt(0)}</span>
                            {u.fullName}
                          </div>
                        </td>
                        <td>{u.designation}</td>
                        <td>{u.company}</td>
                        <td>{u.contact}</td>
                        <td>{u.personalEmail}</td>
                        <td>{u.officialEmail}</td>
                        <td>
                          {u.emailSent ? (
                            <span className="hrd-status-pill hrd-status-pill--sent">
                              ✓ Sent
                            </span>
                          ) : (
                            <span className="hrd-status-pill hrd-status-pill--unsent">
                              ✕ UnSent
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="hrd-send-confirm-btn"
                            disabled={sendingId === u.id}
                            onClick={() => handleSendConfirmation(u.id)}
                          >
                            <Send size={13} />
                            {sendingId === u.id ? "Sending..." : "Send Confirmation"}
                          </button>
                        </td>
                        <td>
                          <div className="hrd-row-actions">
                            <button className="hrd-icon-btn hrd-icon-btn--edit" title="Edit">
                              <Pencil size={13} />
                              Edit
                            </button>
                            <button
                              className="hrd-icon-btn hrd-icon-btn--delete"
                              title="Delete"
                              onClick={() => handleDelete(u.id)}
                            >
                              <Trash2 size={13} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer / pagination */}
          <div className="hrd-table-footer">
            <span>
              Showing {filteredUsers.length} of {total} Members
            </span>
            <div className="hrd-pagination">
              <button className="hrd-page-btn">
                <ChevronLeft size={14} />
              </button>
              <button className="hrd-page-btn active">1</button>
              <button className="hrd-page-btn">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}